/**
 * Widget Registry
 *
 * Defines available widget types and their configuration schemas.
 * Each widget type has:
 * - id: unique identifier for the widget type
 * - name: human-readable display name
 * - description: brief description of what the widget shows
 * - icon: FontAwesome icon name
 * - component: path to the Vue component (for dynamic import)
 * - configComponent: path to the Vue component for configuration (for dynamic import)
 * - configSchema: defines the configuration options for the widget
 * - defaultLayout: default grid layout (x, y, w, h)
 */

import {
  DASHBOARD_TIME_RANGE_OPTIONS,
  REFRESH_INTERVAL_OPTIONS,
  DEFAULT_TIME_RANGE,
  DEFAULT_REFRESH_INTERVAL
} from '@/config/dashboardOptions'
import { HEATMAP_TIMEZONES, defaultTimezone } from '@/utils/timeOfWeekHeatmap'

const widgetTypes = [
  {
    id: 'event_count',
    name: 'Event Count',
    description: 'Displays the total count of events matching a saved search',
    icon: 'hashtag',
    category: 'Metrics',
    component: () => import('./widgets/EventCountWidget.vue'),
    configComponent: () => import('./widgets/config/EventCountWidgetConfig.vue'),
    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Event Count',
        required: true
      },
      timeRange: {
        type: 'enum',
        label: 'Time Range',
        options: DASHBOARD_TIME_RANGE_OPTIONS,
        default: DEFAULT_TIME_RANGE
      },
      logTypes: {
        type: 'multiselect',
        label: 'Log Types',
        options: [], // Dynamically populated from API
        default: []  // Empty = all log types
      },
      showChange: {
        type: 'boolean',
        label: 'Show Period Comparison',
        default: true,
        description: 'Display change compared to previous period'
      },
      changeDisplayMode: {
        type: 'enum',
        label: 'Change Display',
        options: [
          { value: 'percentage', label: 'Percentage' },
          { value: 'absolute', label: 'Absolute Count' }
        ],
        default: 'percentage',
        description: 'Show percentage change or absolute count difference'
      },
      refreshInterval: {
        type: 'enum',
        label: 'Refresh Rate',
        options: REFRESH_INTERVAL_OPTIONS,
        default: DEFAULT_REFRESH_INTERVAL
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 3, h: 2 }
  },
  {
    id: 'event_histogram',
    name: 'Event Histogram',
    description: 'Shows event frequency over time as a bar chart',
    icon: 'chart-bar',
    category: 'Charts',
    component: () => import('./widgets/EventHistogramWidget.vue'),
    configComponent: () => import('./widgets/config/EventHistogramWidgetConfig.vue'),
    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Event Timeline',
        required: true
      },
      timeRange: {
        type: 'enum',
        label: 'Time Range',
        options: DASHBOARD_TIME_RANGE_OPTIONS,
        default: DEFAULT_TIME_RANGE
      },
      displayMode: {
        type: 'enum',
        label: 'Display Mode',
        options: [
          { value: 'sectioned', label: 'Sectioned (by log type)' },
          { value: 'gradient', label: 'Gradient (aggregated)' }
        ],
        default: 'sectioned'
      },
      logTypes: {
        type: 'multiselect',
        label: 'Log Types',
        options: [], // Dynamically populated from API
        default: []  // Empty = all log types
      },
      refreshInterval: {
        type: 'enum',
        label: 'Refresh Rate',
        options: REFRESH_INTERVAL_OPTIONS,
        default: DEFAULT_REFRESH_INTERVAL
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 6, h: 3 }
  },
  {
    id: 'event_table',
    name: 'Event Table',
    description: 'Displays events in a detailed table with pagination',
    icon: 'table',
    category: 'Tables',
    component: () => import('./widgets/EventTableWidget.vue'),
    configComponent: () => import('./widgets/config/EventTableWidgetConfig.vue'),
    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Events',
        required: true
      },
      logType: {
        type: 'enum',
        label: 'Log Type',
        options: [], // Dynamically populated from API
        default: '',
        required: true
      },
      timeRange: {
        type: 'enum',
        label: 'Time Range',
        options: DASHBOARD_TIME_RANGE_OPTIONS,
        default: DEFAULT_TIME_RANGE
      },
      columns: {
        type: 'multiselect',
        label: 'Columns',
        options: [], // Dynamically populated based on selected logType
        default: [],  // Empty = show all columns
        dependsOn: 'logType'
      },
      pageSize: {
        type: 'number',
        label: 'Events per Page',
        default: 25,
        min: 10,
        max: 100
      },
      refreshInterval: {
        type: 'enum',
        label: 'Refresh Rate',
        options: REFRESH_INTERVAL_OPTIONS,
        default: DEFAULT_REFRESH_INTERVAL
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 12, h: 5 }
  },
  {
    id: 'log_type_distribution',
    name: 'Log Type Distribution',
    description: 'Pie chart showing distribution of log types',
    icon: 'chart-pie',
    category: 'Charts',
    component: () => import('./widgets/LogTypeDistributionWidget.vue'),
    configComponent: () => import('./widgets/config/LogTypeDistributionWidgetConfig.vue'),
    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Log Types',
        required: true
      },
      showLegend: {
        type: 'boolean',
        label: 'Show Legend',
        default: true
      },
      logTypes: {
        type: 'multiselect',
        label: 'Log Types',
        options: [],
        default: []
      },
      timeRange: {
        type: 'enum',
        label: 'Time Range',
        options: DASHBOARD_TIME_RANGE_OPTIONS,
        default: DEFAULT_TIME_RANGE
      },
      refreshInterval: {
        type: 'enum',
        label: 'Refresh Rate',
        options: REFRESH_INTERVAL_OPTIONS,
        default: DEFAULT_REFRESH_INTERVAL
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 4, h: 3 }
  },
  {
    id: 'map',
    name: 'Map',
    description: 'World map colored by event count per country',
    icon: 'globe',
    category: 'Charts',
    component: () => import('./widgets/MapWidget.vue'),
    configComponent: () => import('./widgets/config/MapWidgetConfig.vue'),
    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Geographic Distribution',
        required: true
      },
      field: {
        type: 'enum',
        label: 'Country Code Field',
        options: [], // Dynamically populated from geo annotations
        default: '',
        required: true
      },
      showCountriesList: {
        type: 'boolean',
        label: 'Show Countries List',
        default: true
      },
      timeRange: {
        type: 'enum',
        label: 'Time Range',
        options: DASHBOARD_TIME_RANGE_OPTIONS,
        default: DEFAULT_TIME_RANGE
      },
      refreshInterval: {
        type: 'enum',
        label: 'Refresh Rate',
        options: REFRESH_INTERVAL_OPTIONS,
        default: DEFAULT_REFRESH_INTERVAL
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 6, h: 4 }
  },
  {
    id: 'top_values',
    name: 'Top Values',
    description: 'Shows most common values for a specific field',
    icon: 'list-ol',
    category: 'Metrics',
    component: () => import('./widgets/TopValuesWidget.vue'),
    configComponent: () => import('./widgets/config/TopValuesWidgetConfig.vue'),
    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Top Values',
        required: true
      },
      timeRange: {
        type: 'enum',
        label: 'Time Range',
        options: DASHBOARD_TIME_RANGE_OPTIONS,
        default: DEFAULT_TIME_RANGE
      },
      logType: {
        type: 'enum',
        label: 'Log Type',
        options: [],
        default: '',
        required: true
      },
      field: {
        type: 'enum',
        label: 'Field',
        options: [],
        default: '',
        required: true,
        dependsOn: 'logType'
      },
      limit: {
        type: 'enum',
        label: 'Number of Values',
        options: [
          { value: 10, label: '10' },
          { value: 20, label: '20' },
          { value: 30, label: '30' },
          { value: 40, label: '40' },
          { value: 50, label: '50' }
        ],
        default: 10
      },
      refreshInterval: {
        type: 'enum',
        label: 'Refresh Rate',
        options: REFRESH_INTERVAL_OPTIONS,
        default: DEFAULT_REFRESH_INTERVAL
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 4, h: 3 }
  },
  {
    id: 'significant_values',
    name: 'Significant Values',
    description: 'Shows field values that are statistically significant compared to a baseline period',
    icon: 'chart-line',
    category: 'Metrics',
    component: () => import('./widgets/SignificantValuesWidget.vue'),
    configComponent: () => import('./widgets/config/SignificantValuesWidgetConfig.vue'),
    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Significant Values',
        required: true
      },
      timeRange: {
        type: 'enum',
        label: 'Time Range',
        options: DASHBOARD_TIME_RANGE_OPTIONS, // capped list already excludes 'all'
        default: DEFAULT_TIME_RANGE
      },
      baselinePeriod: {
        type: 'enum',
        label: 'Baseline Period',
        options: [
          { value: 'auto', label: 'Auto (based on time range)' },
          { value: '24h', label: '24 hours' },
          { value: '7d', label: '7 days' },
          { value: '30d', label: '30 days' },
          { value: '90d', label: '90 days' },
          { value: '180d', label: '180 days' },
          { value: '365d', label: '1 year' }
        ],
        default: 'auto'
      },
      logType: {
        type: 'enum',
        label: 'Log Type',
        options: [],
        default: '',
        required: true
      },
      field: {
        type: 'enum',
        label: 'Field',
        options: [],
        default: '',
        required: true,
        dependsOn: 'logType'
      },
      limit: {
        type: 'enum',
        label: 'Number of Values',
        options: [
          { value: 5, label: '5' },
          { value: 10, label: '10' },
          { value: 20, label: '20' },
          { value: 30, label: '30' }
        ],
        default: 10
      },
      refreshInterval: {
        type: 'enum',
        label: 'Refresh Rate',
        options: REFRESH_INTERVAL_OPTIONS,
        default: DEFAULT_REFRESH_INTERVAL
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 4, h: 3 }
  },
  {
    id: 'time_of_week_heatmap',
    name: 'Time-of-Week Heatmap',
    description: 'Event activity by day of week and hour of day — reveals actor timezone and scripted vs. handheld patterns',
    icon: 'table-cells-large',
    category: 'Charts',
    component: () => import('./widgets/TimeOfWeekHeatmapWidget.vue'),
    configComponent: () => import('./widgets/config/TimeOfWeekHeatmapWidgetConfig.vue'),
    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Time-of-Week Activity',
        required: true
      },
      timeRange: {
        type: 'enum',
        label: 'Time Range',
        options: DASHBOARD_TIME_RANGE_OPTIONS,
        default: '30d'
      },
      logType: {
        type: 'enum',
        label: 'Log Type',
        options: [], // Optional filter; populated via QuerySourceSection
        default: ''
      },
      timezone: {
        type: 'enum',
        label: 'Timezone',
        options: HEATMAP_TIMEZONES,
        default: defaultTimezone()
      },
      refreshInterval: {
        type: 'enum',
        label: 'Refresh Rate',
        options: REFRESH_INTERVAL_OPTIONS,
        default: DEFAULT_REFRESH_INTERVAL
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 6, h: 4 }
  },
  {
    id: 'text',
    name: 'Text',
    description: 'Display custom markdown content',
    icon: 'align-left',
    category: 'Misc',
    component: () => import('./widgets/TextWidget.vue'),
    configComponent: () => import('./widgets/config/TextWidgetConfig.vue'),

    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Text',
        required: true
      },
      content: {
        type: 'textarea',
        label: 'Markdown Content',
        default: '# Welcome\n\nAdd your markdown content here...',
        required: false,
        placeholder: 'Enter markdown content...',
        rows: 10,
        maxLength: 50000
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 6, h: 4 }
  },
  {
    id: 'divider',
    name: 'Divider',
    description: 'Horizontal separator line',
    icon: 'minus',
    category: 'Misc',
    component: () => import('./widgets/DividerWidget.vue'),
    configComponent: () => import('./widgets/config/DividerWidgetConfig.vue'),

    configSchema: {
      title: {
        type: 'string',
        label: 'Title',
        default: 'Divider',
        required: false
      },
      hideHeader: {
        type: 'boolean',
        label: 'Hide Header',
        default: false
      },
      transparentContainer: {
        type: 'boolean',
        label: 'Transparent Container',
        default: false
      }
    },
    defaultLayout: { x: 0, y: 0, w: 12, h: 1 }
  }
]

/**
 * Get all available widget types
 * @returns {Array} Array of widget type definitions
 */
export function getWidgetTypes() {
  return widgetTypes
}

/**
 * Get a specific widget type by ID
 * @param {string} typeId - The widget type ID
 * @returns {Object|undefined} The widget type definition or undefined
 */
export function getWidgetType(typeId) {
  return widgetTypes.find(wt => wt.id === typeId)
}

/**
 * Get the component loader for a widget type
 * @param {string} typeId - The widget type ID
 * @returns {Function|null} The component loader function or null
 */
export function getWidgetComponent(typeId) {
  const widgetType = getWidgetType(typeId)
  return widgetType ? widgetType.component : null
}

/**
 * Get the config schema for a widget type
 * @param {string} typeId - The widget type ID
 * @returns {Object|null} The config schema or null
 */
export function getConfigSchema(typeId) {
  const widgetType = getWidgetType(typeId)
  return widgetType ? widgetType.configSchema : null
}

/**
 * Get default config values for a widget type
 * @param {string} typeId - The widget type ID
 * @returns {Object} Object with default config values
 */
export function getDefaultConfig(typeId) {
  const schema = getConfigSchema(typeId)
  if (!schema) return {}

  const defaults = {}
  for (const [key, field] of Object.entries(schema)) {
    if (field.default !== undefined) {
      defaults[key] = field.default
    }
  }
  return defaults
}

/**
 * Get the default layout for a widget type
 * @param {string} typeId - The widget type ID
 * @returns {Object} Default layout object
 */
export function getDefaultLayout(typeId) {
  const widgetType = getWidgetType(typeId)
  return widgetType ? { ...widgetType.defaultLayout } : { x: 0, y: 0, w: 4, h: 3 }
}

/**
 * Get the config component loader for a widget type
 * @param {string} typeId - The widget type ID
 * @returns {Function|null} The config component loader function or null
 */
export function getWidgetConfigComponent(typeId) {
  const widgetType = getWidgetType(typeId)
  return widgetType ? widgetType.configComponent : null
}

export default {
  getWidgetTypes,
  getWidgetType,
  getWidgetComponent,
  getWidgetConfigComponent,
  getConfigSchema,
  getDefaultConfig,
  getDefaultLayout
}
